import { css } from 'styled-components';

/**
 * Workforce design tokens.
 *
 * Same brand values as the Screening kit (navy, paper, canvas, hairline,
 * Space Grotesk / DM Sans, near-square radii, a single overlay shadow) so the
 * two products read as one HireMe family, published under `--workforce-*`
 * names so the products can drift independently. Never mutates the global,
 * `--agency-*`, or `--screening-*` variables.
 */
const WorkforceVariables = css`
  :root {
    /* Palette (brand-aligned) */
    --workforce-ink: #10151c;
    --workforce-navy: #032170;
    --workforce-navy-deep: #041a4d;
    --workforce-blue-grey: #bacdd1;
    --workforce-paper: #ffffff;
    --workforce-canvas: #f6f8f9;
    --workforce-hairline: #e4e8ec;
    --workforce-muted: #5c6773;

    /* Desaturated, print-like status palette (never neon) */
    --workforce-confirm: #1b6b52;
    --workforce-confirm-wash: #eaf1ee;
    --workforce-attention: #8a6a1b;
    --workforce-attention-wash: #f4efe2;
    --workforce-problem: #9a3934;
    --workforce-problem-wash: #f3e7e6;

    /* Typography — brand faces already loaded globally */
    --workforce-font-display: 'Space Grotesk', 'DM Sans', sans-serif;
    --workforce-font-ui: 'DM Sans', 'Inter', sans-serif;

    /* Near-square radii */
    --workforce-r-card: 6px;
    --workforce-r-input: 4px;
    --workforce-r-chip: 4px;

    /* Exactly one shadow, reserved for true floating overlays */
    --workforce-shadow-overlay: 0 12px 32px -12px rgba(4, 26, 77, 0.28);
  }

  .workforce-root {
    font-family: var(--workforce-font-ui);
    color: var(--workforce-ink);
    background: var(--workforce-canvas);
  }

  .workforce-root *,
  .workforce-root *::before,
  .workforce-root *::after {
    box-sizing: border-box;
  }

  /* Tabular lining figures for numeric runs (counts, cadences, scores) */
  .workforce-tnum {
    font-feature-settings:
      'tnum' 1,
      'lnum' 1;
    font-variant-numeric: tabular-nums lining-nums;
  }

  /* Grow with the page so tables are not trapped in a 100vh clip. */
  html:has(.workforce-root),
  html:has(.workforce-root) body {
    height: auto;
    min-height: 100%;
    overflow: visible;
  }

  /* Thin flat scrollbars, scoped like the screening kit */
  html:has(.workforce-root),
  html:has(.workforce-root) body,
  .workforce-root {
    scrollbar-width: thin;
    scrollbar-color: var(--workforce-hairline) transparent;
  }

  html:has(.workforce-root)::-webkit-scrollbar,
  html:has(.workforce-root) body::-webkit-scrollbar,
  .workforce-root *::-webkit-scrollbar,
  .workforce-root::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  html:has(.workforce-root)::-webkit-scrollbar-track,
  html:has(.workforce-root) body::-webkit-scrollbar-track,
  .workforce-root *::-webkit-scrollbar-track,
  .workforce-root::-webkit-scrollbar-track {
    background: transparent;
  }

  html:has(.workforce-root)::-webkit-scrollbar-thumb,
  html:has(.workforce-root) body::-webkit-scrollbar-thumb,
  .workforce-root *::-webkit-scrollbar-thumb,
  .workforce-root::-webkit-scrollbar-thumb {
    background: var(--workforce-hairline);
    border-radius: 999px;
    box-shadow: none;
  }
`;

export default WorkforceVariables;
